import { API_ENDPOINTS } from '../config/api'

async function parseJson(res: Response) {
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(json?.message || json?.error || 'Request failed')
  }
  return json?.data ?? json
}

export type SessionLifecycle = {
  sessionId: string
  standardCode: string | null
  lifecycleStatus: string
  assessmentCycle: number
  assessmentVersion: number
  previousSessionId: string | null
  remediationMode?: boolean
  allowedTransitions?: string[]
  resumeControlKey?: string | null
}

export type UnresolvedControlRow = {
  controlKey: string
  controlTitle?: string
  status: string
  answerStatus?: string
  evidenceStatus?: string
  severity?: string
  remediationRoute?: string
  questionId?: string | null
  domain?: string
  lastUpdatedAt?: string | null
}

export type UnresolvedControlsPayload = {
  sessionId: string
  standardCode: string
  controls: UnresolvedControlRow[]
  resumeControlKey?: string | null
  savedCursor?: {
    currentControlKey?: string | null
    currentQuestionId?: string | null
    mode?: string
  } | null
}

export type PlatformEvidenceCard = {
  sourceType: string
  platformSource?: string
  label?: string
  description?: string
  artifacts?: string[]
  status?: string
  verified?: boolean
  section?: 'recommended' | 'other'
  alreadyLinked?: boolean
}

export type PlatformEvidenceResponse = {
  standardCode: string
  controlKey: string
  recommended?: PlatformEvidenceCard[]
  other?: PlatformEvidenceCard[]
  mappings?: PlatformEvidenceCard[]
  hasAny?: boolean
}

export async function fetchSessionLifecycle(token: string, sessionId: string): Promise<SessionLifecycle> {
  const res = await fetch(API_ENDPOINTS.COMPLIANCE_SESSION_LIFECYCLE(sessionId), {
    headers: { Authorization: `Bearer ${token}` },
  })
  return parseJson(res)
}

export async function reopenAssessmentSession(token: string, sessionId: string): Promise<SessionLifecycle> {
  const res = await fetch(API_ENDPOINTS.COMPLIANCE_SESSION_REOPEN(sessionId), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  })
  return parseJson(res)
}

export async function continueRemediationSession(token: string, sessionId: string) {
  const res = await fetch(API_ENDPOINTS.COMPLIANCE_SESSION_CONTINUE_REMEDIATION(sessionId), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  })
  return parseJson(res)
}

export async function startNewAssessmentCycle(token: string, sessionId: string): Promise<SessionLifecycle> {
  const res = await fetch(API_ENDPOINTS.COMPLIANCE_SESSION_NEW_CYCLE(sessionId), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  })
  return parseJson(res)
}

export async function fetchUnresolvedControls(
  token: string,
  sessionId: string,
): Promise<UnresolvedControlsPayload> {
  const res = await fetch(API_ENDPOINTS.COMPLIANCE_SESSION_UNRESOLVED_CONTROLS(sessionId), {
    headers: { Authorization: `Bearer ${token}` },
  })
  return parseJson(res)
}

export async function saveAssessmentProgress(
  token: string,
  sessionId: string,
  body: {
    answers?: Array<Record<string, unknown>>
    linkedEvidence?: string[]
    currentControlKey?: string
    currentQuestionId?: string
    domainKey?: string
    answerValue?: string
    mode?: 'ASSESSMENT' | 'REMEDIATION'
  },
) {
  const res = await fetch(API_ENDPOINTS.COMPLIANCE_SESSION_SAVE_PROGRESS(sessionId), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return parseJson(res)
}

export async function fetchPlatformEvidence(
  token: string,
  params: { standardCode: string; controlKey?: string; sessionId?: string; questionId?: string },
): Promise<PlatformEvidenceResponse> {
  const q = new URLSearchParams({ standardCode: params.standardCode })
  if (params.controlKey) q.set('controlKey', params.controlKey)
  if (params.sessionId) q.set('sessionId', params.sessionId)
  if (params.questionId) q.set('questionId', params.questionId)
  const res = await fetch(`${API_ENDPOINTS.COMPLIANCE_PLATFORM_EVIDENCE}?${q}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return parseJson(res)
}

export async function linkPlatformEvidence(
  token: string,
  body: {
    standardCode: string
    controlKey: string
    platformSource: string
    sessionId?: string
    responseId?: string
    questionId?: string
  },
) {
  const res = await fetch(API_ENDPOINTS.COMPLIANCE_PLATFORM_EVIDENCE_LINK, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return parseJson(res)
}

export async function fetchComplianceOperationsWorkspace(token: string) {
  const res = await fetch(API_ENDPOINTS.COMPLIANCE_OPERATIONS_WORKSPACE, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return parseJson(res)
}

export async function fetchAssessmentTimeline(token: string, standardCode?: string | null) {
  const q = standardCode ? `?standardCode=${encodeURIComponent(standardCode)}` : ''
  const res = await fetch(`${API_ENDPOINTS.COMPLIANCE_ASSESSMENT_TIMELINE}${q}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return parseJson(res)
}

export async function fetchEnabledStandards(token: string) {
  const res = await fetch(API_ENDPOINTS.COMPLIANCE_ENABLED_STANDARDS, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return parseJson(res)
}
